import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { CustomerSalesPerformanceDto, GridRowData, ItemSalesPerformanceDto } from './Dto_SAL2050_R01';
import { SAL2050_R01SubtotalRow } from './Dto_SAL2050_R01';

export type SAL2050_R01TabType = 'customer' | 'item';

export interface SAL2050_R01TabConfig {
	id: SAL2050_R01TabType;
	label: string;
	description: string;
}

export interface SAL2050_R01TabData {
	customer: CustomerSalesPerformanceDto[];
	item: ItemSalesPerformanceDto[];
}

export interface SAL2050_R01TabGridData {
	customer: GridRowData[];
	item: GridRowData[];
}

export interface SAL2050_R01TabSubtotalData {
	customer: SAL2050_R01SubtotalRow[];
	item: SAL2050_R01SubtotalRow[];
}

export interface SAL2050_R01TabColumnStates {
	customer: ColumnState[];
	item: ColumnState[];
}

export interface SAL2050_R01TabLoadingStates {
	customer: boolean;
	item: boolean;
}

export interface SAL2050_R01TabSelectedIds {
	customer: string[];
	item: string[];
}

export interface SAL2050_R01TabScrollTop {
	customer: number | null;
	item: number | null;
}

const defaultSAL2050_R01TabScrollTop = (): SAL2050_R01TabScrollTop => ({
	customer: null,
	item: null,
});

export interface SAL2050_R01Filter {
	compCd: string;
	customCd: string;
	customNm: string;
	brandCd: string;
	itemCd: string;
	itemNm: string;
	salesYear: string;
}

interface SAL2050_R01Store {
	SAL2050_R01Filter: SAL2050_R01Filter;
	setSAL2050_R01Filter: (filter: Partial<SAL2050_R01Filter>) => void;

	SAL2050_R01activeTab: SAL2050_R01TabType;
	setSAL2050_R01ActiveTab: (tab: SAL2050_R01TabType) => void;

	SAL2050_R01tabData: SAL2050_R01TabData;
	setSAL2050_R01TabData: (tabType: SAL2050_R01TabType, data: any[]) => void;

	SAL2050_R01tabGridData: SAL2050_R01TabGridData;
	setSAL2050_R01TabGridData: (tabType: SAL2050_R01TabType, data: GridRowData[]) => void;

	SAL2050_R01tabSubtotalData: SAL2050_R01TabSubtotalData;
	setSAL2050_R01TabSubtotalData: (tabType: SAL2050_R01TabType, data: SAL2050_R01SubtotalRow[]) => void;

	SAL2050_R01showSubtotals: boolean;
	setSAL2050_R01ShowSubtotals: (show: boolean) => void;

	SAL2050_R01tabColumnStates: SAL2050_R01TabColumnStates;
	setSAL2050_R01TabColumnState: (tabType: SAL2050_R01TabType, columnState: any[]) => void;

	SAL2050_R01tabLoadingStates: SAL2050_R01TabLoadingStates;
	setSAL2050_R01TabLoadingState: (tabType: SAL2050_R01TabType, loading: boolean) => void;

	SAL2050_R01tabSelectedIds: SAL2050_R01TabSelectedIds;
	setSAL2050_R01TabSelectedIds: (tabType: SAL2050_R01TabType, ids: string[]) => void;
	SAL2050_R01toggleSelectedId: (tabType: SAL2050_R01TabType, id: string) => void;
	SAL2050_R01clearTabSelectedIds: (tabType: SAL2050_R01TabType) => void;

	SAL2050_R01tabScrollTop: SAL2050_R01TabScrollTop;
	setSAL2050_R01TabScrollTop: (tabType: SAL2050_R01TabType, top: number | null) => void;
	resetAllSAL2050_R01TabScrollTop: () => void;

	initData: () => void;
}

export const defaultSAL2050_R01Filter: SAL2050_R01Filter = {
	compCd: '',
	customCd: '',
	customNm: '',
	brandCd: '',
	itemCd: '',
	itemNm: '',
	salesYear: String(new Date().getFullYear()),
};

const defaultTabData = (): SAL2050_R01TabData => ({
	customer: [],
	item: [],
});

const defaultTabGridData = (): SAL2050_R01TabGridData => ({
	customer: [],
	item: [],
});

const defaultTabSubtotalData = (): SAL2050_R01TabSubtotalData => ({
	customer: [],
	item: [],
});

const defaultTabColumnStates = (): SAL2050_R01TabColumnStates => ({
	customer: [],
	item: [],
});

const defaultTabLoadingStates = (): SAL2050_R01TabLoadingStates => ({
	customer: false,
	item: false,
});

const defaultTabSelectedIds = (): SAL2050_R01TabSelectedIds => ({
	customer: [],
	item: [],
});

export const useSAL2050_R01Store = create<SAL2050_R01Store>((set) => ({
	SAL2050_R01Filter: { ...defaultSAL2050_R01Filter },
	setSAL2050_R01Filter: (filter) => set((state) => ({
		SAL2050_R01Filter: { ...state.SAL2050_R01Filter, ...filter }
	})),

	SAL2050_R01activeTab: 'customer',
	setSAL2050_R01ActiveTab: (tab) => {
		set({ SAL2050_R01activeTab: tab });
	},

	SAL2050_R01tabData: defaultTabData(),
	setSAL2050_R01TabData: (tabType, data) => set((state) => ({
		SAL2050_R01tabData: {
			...state.SAL2050_R01tabData,
			[tabType]: data
		}
	})),

	SAL2050_R01tabGridData: defaultTabGridData(),
	setSAL2050_R01TabGridData: (tabType, data) => set((state) => ({
		SAL2050_R01tabGridData: {
			...state.SAL2050_R01tabGridData,
			[tabType]: data
		}
	})),

	SAL2050_R01tabSubtotalData: defaultTabSubtotalData(),
	setSAL2050_R01TabSubtotalData: (tabType, data) => set((state) => ({
		SAL2050_R01tabSubtotalData: {
			...state.SAL2050_R01tabSubtotalData,
			[tabType]: data
		}
	})),

	SAL2050_R01showSubtotals: false,
	setSAL2050_R01ShowSubtotals: (show) => set({ SAL2050_R01showSubtotals: show }),

	SAL2050_R01tabColumnStates: defaultTabColumnStates(),
	setSAL2050_R01TabColumnState: (tabType, columnState) => set((state) => ({
		SAL2050_R01tabColumnStates: {
			...state.SAL2050_R01tabColumnStates,
			[tabType]: columnState
		}
	})),

	SAL2050_R01tabLoadingStates: defaultTabLoadingStates(),
	setSAL2050_R01TabLoadingState: (tabType, loading) => set((state) => ({
		SAL2050_R01tabLoadingStates: {
			...state.SAL2050_R01tabLoadingStates,
			[tabType]: loading
		}
	})),

	SAL2050_R01tabSelectedIds: defaultTabSelectedIds(),
	setSAL2050_R01TabSelectedIds: (tabType, ids) => set((state) => ({
		SAL2050_R01tabSelectedIds: {
			...state.SAL2050_R01tabSelectedIds,
			[tabType]: ids
		}
	})),
	SAL2050_R01toggleSelectedId: (tabType, id) => set((state) => {
		const current = state.SAL2050_R01tabSelectedIds[tabType] || [];
		const exists = current.includes(id);
		const next = exists ? current.filter(v => v !== id) : [...current, id];
		return {
			SAL2050_R01tabSelectedIds: {
				...state.SAL2050_R01tabSelectedIds,
				[tabType]: next
			}
		};
	}),
	SAL2050_R01clearTabSelectedIds: (tabType) => set((state) => ({
		SAL2050_R01tabSelectedIds: {
			...state.SAL2050_R01tabSelectedIds,
			[tabType]: []
		}
	})),

	SAL2050_R01tabScrollTop: defaultSAL2050_R01TabScrollTop(),
	setSAL2050_R01TabScrollTop: (tabType, top) => set((state) => ({
		SAL2050_R01tabScrollTop: {
			...state.SAL2050_R01tabScrollTop,
			[tabType]: top,
		},
	})),
	resetAllSAL2050_R01TabScrollTop: () => set({ SAL2050_R01tabScrollTop: defaultSAL2050_R01TabScrollTop() }),

	initData: () => set({
		SAL2050_R01Filter: { ...defaultSAL2050_R01Filter },
		SAL2050_R01activeTab: 'customer',
		SAL2050_R01tabData: defaultTabData(),
		SAL2050_R01tabGridData: defaultTabGridData(),
		SAL2050_R01tabSubtotalData: defaultTabSubtotalData(),
		SAL2050_R01showSubtotals: false,
		SAL2050_R01tabColumnStates: defaultTabColumnStates(),
		SAL2050_R01tabLoadingStates: defaultTabLoadingStates(),
		SAL2050_R01tabSelectedIds: defaultTabSelectedIds(),
		SAL2050_R01tabScrollTop: defaultSAL2050_R01TabScrollTop(),
	}),
}));

export default useSAL2050_R01Store;
